import React, { useState, useEffect } from 'react';
import { ChevronUp, ChevronDown, Users2, Info, Copy } from 'lucide-react';
import { Button, Tooltip } from 'antd';
import { useTheme } from '../../../contexts/ThemeContext';

const RatingSlider = ({ value, onChange, onCommit, disabled, accent, isDark }) => {
  const trackBg = isDark ? '#1f2937' : '#f1f5f9';
  return (
    <input
      type="range"
      min={0}
      max={10}
      step={1}
      value={value || 0}
      disabled={disabled}
      onChange={(e) => onChange && onChange(parseInt(e.target.value, 10))}
      onMouseUp={(e) => onCommit && onCommit(parseInt(e.target.value, 10))}
      onTouchEnd={(e) => onCommit && onCommit(parseInt(e.target.value, 10))}
      onKeyUp={(e) => onCommit && onCommit(parseInt(e.target.value, 10))}
      className={`flex-1 h-1.5 rounded-full appearance-none cursor-pointer ${disabled ? 'cursor-not-allowed' : ''}`}
      style={{ accentColor: accent, background: trackBg }}
    />
  );
};

const ValueChip = ({ value, accent, mutedAccent, isDark, disabled }) => {
  const hasValue = value > 0;
  const bg = disabled
    ? (isDark ? 'rgba(255,255,255,0.05)' : '#f8fafc')
    : hasValue
      ? (isDark ? `${accent}22` : mutedAccent)
      : (isDark ? 'rgba(255,255,255,0.05)' : '#f8fafc');
  const border = disabled
    ? (isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e2e8f0')
    : hasValue
      ? `1px solid ${accent}55`
      : (isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e2e8f0');
  const textColor = disabled
    ? (isDark ? 'rgba(255,255,255,0.4)' : '#94a3b8')
    : hasValue
      ? accent
      : (isDark ? 'rgba(255,255,255,0.4)' : '#cbd5e1');
  return (
    <div
      className="w-9 h-9 rounded-full flex items-center justify-center text-[13px] font-bold shrink-0"
      style={{ background: bg, border, color: textColor }}
    >
      {value || 0}
    </div>
  );
};

const SoftSkillsSection = ({
  softSkillsData = [],
  onRatingChange,
  isExpanded: externalIsExpanded,
  onToggle,
  canEditEmployeeRating = false,
  canEditManagerRating = false,
}) => {
  const { mode } = useTheme();
  const isDark = mode === 'dark';
  const [isExpanded, setIsExpanded] = useState(externalIsExpanded !== undefined ? externalIsExpanded : false);

  useEffect(() => {
    if (externalIsExpanded !== undefined) setIsExpanded(externalIsExpanded);
  }, [externalIsExpanded]);

  const hasAllEmployeeRatings = softSkillsData.length > 0 && softSkillsData.every(s => (s.employeeRating || 0) > 0);
  const hasAllManagerRatings = softSkillsData.length > 0 && softSkillsData.every(s => (s.managerRating || 0) > 0);

  const [isEmployeeSubmitted, setIsEmployeeSubmitted] = useState(hasAllEmployeeRatings);
  const [isManagerSubmitted, setIsManagerSubmitted] = useState(hasAllManagerRatings);
  const [userInteracted, setUserInteracted] = useState(false);

  useEffect(() => {
    if (!userInteracted) {
      if (hasAllEmployeeRatings) setIsEmployeeSubmitted(true);
      if (hasAllManagerRatings) setIsManagerSubmitted(true);
    }
  }, [hasAllEmployeeRatings, hasAllManagerRatings, userInteracted]);

  const [localRatings, setLocalRatings] = useState({});
  useEffect(() => {
    const next = {};
    softSkillsData.forEach(skill => {
      next[`employee-${skill.id}`] = skill.employeeRating;
      next[`manager-${skill.id}`] = skill.managerRating;
    });
    setLocalRatings(next);
  }, [softSkillsData]);

  const handleToggle = () => {
    const next = !isExpanded;
    setIsExpanded(next);
    if (onToggle) onToggle(next);
  };

  const liveValue = (skillId, type, fallback) => {
    const k = `${type}-${skillId}`;
    return localRatings[k] !== undefined ? localRatings[k] : fallback;
  };

  const employeeEditable = canEditEmployeeRating && !isEmployeeSubmitted;
  const managerEditable = canEditManagerRating && !isManagerSubmitted;

  // Copy employee self-assessment values into the manager evaluation column.
  // Only meaningful when the manager is editing and the employee has at least
  // one rating > 0 to copy.
  const canCopySelfToManager =
    managerEditable &&
    softSkillsData.length > 0 &&
    softSkillsData.some(s => (s.employeeRating || 0) > 0);

  const handleCopySelfAssessment = () => {
    if (!canCopySelfToManager) return;
    setUserInteracted(true);
    const next = { ...localRatings };
    softSkillsData.forEach(skill => {
      const empVal = skill.employeeRating || 0;
      next[`manager-${skill.id}`] = empVal;
      if (onRatingChange) onRatingChange(skill.id, 'managerRating', empVal);
    });
    setLocalRatings(next);
  };

  const surface = isDark ? 'bg-white/[0.03] border-white/10' : 'bg-white border-slate-200';
  const headerBg = isDark ? 'bg-white/[0.04] hover:bg-white/[0.06]' : 'bg-white hover:bg-slate-50';
  const dividerColor = isDark ? 'divide-white/5' : 'divide-slate-100';
  const subtleText = isDark ? 'text-white/50' : 'text-slate-500';
  const primaryText = isDark ? 'text-white' : 'text-slate-900';
  const employeeAccent = '#2563eb';
  const employeeMuted = '#eff6ff';
  const managerAccent = '#0f766e';
  const managerMuted = '#ecfdf5';

  return (
    <div
      className={`rounded-xl border overflow-hidden shadow-sm ${surface}`}
      style={{ fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif' }}
    >
      {/* Header */}
      <div
        onClick={handleToggle}
        className={`flex items-center justify-between px-6 py-4 cursor-pointer transition-colors group/sec ${headerBg} ${isExpanded ? (isDark ? 'border-b border-white/10' : 'border-b border-slate-200') : ''}`}
      >
        <div className="flex items-center gap-3">
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center shadow-sm ${isDark ? 'bg-emerald-500/15 text-emerald-400' : 'bg-emerald-50 text-emerald-600'}`}>
            <Users2 size={18} />
          </div>
          <h3 className={`text-sm font-bold tracking-tight ${primaryText}`}>
            Soft Skills &amp; Competencies
          </h3>
        </div>
        <div className="flex items-center gap-4">
          <span className={`flex items-center gap-2 h-6 px-2 rounded-full text-[10px] font-bold uppercase tracking-wider ${isDark ? 'bg-white text-slate-900' : 'bg-slate-900 text-white'}`}>
            {softSkillsData.length} Parameters
          </span>
          {isExpanded
            ? <ChevronUp size={18} className={subtleText} />
            : <ChevronDown size={18} className={subtleText} />}
        </div>
      </div>

      {/* Body */}
      {isExpanded && (
        <div className="px-8 py-6">
          {/* Column headers */}
          <div className={`grid grid-cols-[48px_1fr_1fr_1fr] gap-8 text-[10px] font-bold uppercase tracking-widest py-3 mb-2 px-2 border-b ${isDark ? 'border-white/10 text-white/60' : 'border-slate-200 text-slate-500'}`}>
            <div className="text-center">#</div>
            <div>Competency Area</div>
            <div className="flex items-center justify-center gap-2">
              <span>Self Assessment</span>
              {canCopySelfToManager && (
                <Tooltip title="Copy employee's self assessment into your manager evaluation">
                  <button
                    type="button"
                    onClick={handleCopySelfAssessment}
                    className={`inline-flex items-center gap-1 h-5 px-1.5 rounded-md text-[9px] font-bold uppercase tracking-wider transition-colors ${
                      isDark
                        ? 'bg-blue-500/15 text-blue-300 hover:bg-blue-500/25 border border-blue-500/30'
                        : 'bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200'
                    }`}
                  >
                    <Copy size={10} strokeWidth={2.5} />
                    Copy
                  </button>
                </Tooltip>
              )}
            </div>
            <div className="text-center">Manager Evaluation</div>
          </div>

          {/* Rows */}
          <div className={`divide-y ${dividerColor}`}>
            {softSkillsData.length === 0 ? (
              <div className={`py-10 text-center text-sm ${subtleText}`}>No soft skills configured for this cycle.</div>
            ) : softSkillsData.map((skill, idx) => {
              const empVal = liveValue(skill.id, 'employee', skill.employeeRating || 0);
              const mgrVal = liveValue(skill.id, 'manager', skill.managerRating || 0);
              return (
                <div key={skill.id} className="grid grid-cols-[48px_1fr_1fr_1fr] gap-8 items-center py-5 px-2">
                  <div className={`text-[13px] font-bold text-center ${subtleText}`}>{idx + 1}</div>
                  <div className="flex flex-col">
                    <div className={`flex items-center gap-2 text-[14px] font-bold ${isDark ? 'text-white/90' : 'text-slate-900'}`}>
                      {skill.name}
                      {skill.description && (
                        <Tooltip title={skill.description}>
                          <Info size={14} className={isDark ? 'text-white/30' : 'text-slate-300'} />
                        </Tooltip>
                      )}
                    </div>
                    {skill.description && (
                      <span className={`text-[11px] font-medium leading-tight ${subtleText}`}>{skill.description}</span>
                    )}
                  </div>

                  {/* Employee column */}
                  <div className={`flex items-center gap-4 px-2 ${employeeEditable ? '' : 'opacity-60 pointer-events-none'}`}>
                    <RatingSlider
                      value={empVal}
                      disabled={!employeeEditable}
                      accent={employeeAccent}
                      isDark={isDark}
                      onChange={(v) => {
                        setUserInteracted(true);
                        setLocalRatings(prev => ({ ...prev, [`employee-${skill.id}`]: v }));
                      }}
                      onCommit={(v) => {
                        if (onRatingChange) onRatingChange(skill.id, 'employeeRating', v);
                      }}
                    />
                    <ValueChip value={empVal} accent={employeeAccent} mutedAccent={employeeMuted} isDark={isDark} disabled={!employeeEditable} />
                  </div>

                  {/* Manager column */}
                  <div className={`flex items-center gap-4 px-2 ${managerEditable ? '' : 'opacity-60 pointer-events-none'}`}>
                    <RatingSlider
                      value={mgrVal}
                      disabled={!managerEditable}
                      accent={managerAccent}
                      isDark={isDark}
                      onChange={(v) => {
                        setUserInteracted(true);
                        setLocalRatings(prev => ({ ...prev, [`manager-${skill.id}`]: v }));
                      }}
                      onCommit={(v) => {
                        if (onRatingChange) onRatingChange(skill.id, 'managerRating', v);
                      }}
                    />
                    <ValueChip value={mgrVal} accent={managerAccent} mutedAccent={managerMuted} isDark={isDark} disabled={!managerEditable} />
                  </div>
                </div>
              );
            })}
          </div>

          {/* Submit buttons */}
          {((canEditEmployeeRating && !isEmployeeSubmitted) || (canEditManagerRating && !isManagerSubmitted)) && (
            <div className="flex justify-end mt-6 gap-2">
              {canEditEmployeeRating && !isEmployeeSubmitted && (
                <Button
                  type="primary"
                  onClick={() => setIsEmployeeSubmitted(true)}
                  className="!text-[10px] !font-bold !uppercase !tracking-widest !h-auto !px-5 !py-2.5 !rounded-xl !bg-emerald-600 hover:!bg-emerald-700 !border-0"
                >
                  Submit Your Ratings
                </Button>
              )}
              {canEditManagerRating && !isManagerSubmitted && (
                <Button
                  type="primary"
                  onClick={() => setIsManagerSubmitted(true)}
                  className="!text-[10px] !font-bold !uppercase !tracking-widest !h-auto !px-5 !py-2.5 !rounded-xl !bg-blue-600 hover:!bg-blue-700 !border-0"
                >
                  Submit Manager Ratings
                </Button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SoftSkillsSection;
